import type { SearchHit, SearchRecordType, SearchResult } from "@closer/shared";
import { SEARCH_RECORD_TYPE_NAMES } from "@closer/shared";
import { Badge, Button, Spinner } from "@closer/ui";

import { StateView } from "../components/StateView";
import { resolveErrorState } from "../lib/uiState";

/**
 * ARAMA SONUÇLARI — tür bazlı gruplama (CLOS-184 `[166]`).
 *
 * ### ⚠ Bu bileşen DOMAIN KAYDI OKUMUYOR
 *
 * Kartın bağımlılık sınırı: *"CLOS-184 domain kayıtlarını doğrudan okumaz; CLOS-183
 * tarafından üretilen standart, erişim filtreli sonuç sözleşmesini tüketir."* Burada
 * sözleşme, şablon ya da müvekkil ucuna hiçbir çağrı yok — yalnız `/api/search`.
 *
 * Aksi hâlde erişim filtresi iki yerde kurulurdu ve ikisi bir gün ayrışırdı.
 *
 * ### ⚠ GRUP BAŞLIĞI YALNIZ SONUÇ VARSA ÇİZİLİYOR
 *
 * Kart: *"her sorguda bütün grupların görünmesi gerekmez"* ve *"başka tenant'a ait kayıt
 * varlığı ... grup başlığı ya da SAYAÇTAN sızdırılmaz."* Boş bir "Müvekkiller (0)" başlığı
 * çizseydik, kullanıcının göremediği bir türün VAR olduğunu ele verirdik — üstelik sayaç
 * yazsaydık kaç tane olduğunu da.
 */

const SIRA: SearchRecordType[] = ["contract", "client", "template"];

/** Seçenek kimliği — `aria-activedescendant` ile satır arasındaki bağ. */
export function secenekId(hit: Pick<SearchHit, "recordType" | "recordId">): string {
  return `arama-secenek-${hit.recordType}-${hit.recordId}`;
}

type Durum = {
  data?: SearchResult;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

type SearchResultsProps = {
  /** Kullanıcının yazdığı terim — boş/kısa hâlleri ayırt etmek için. */
  terim: string;
  /** Sorgunun çalışması için gereken en az uzunluk (sunucu konfigüyle aynı ürün kuralı). */
  minUzunluk: number;
  durum: Durum;
  /** Bir sonuca gidildiğinde paneli kapatmak için. */
  onGit: (hit: SearchHit) => void;
  /** Klavye gezinimindeki seçili sıra (−1 = seçim yok). */
  seciliIndeks: number;
  /** Düz listeye çevrilmiş sonuçlar — klavye gezinimi sırayı buradan okuyor. */
  duzListe: SearchHit[];
  /** Seçili tür filtresi; boş sonuç bunu SÖYLEMEK zorunda. */
  turFiltresi: SearchRecordType | null;
  /** Filtreyi kaldıran aksiyon — boş durum çıkmaz sokak olmasın diye. */
  onFiltreTemizle: () => void;
};

export function SearchResults({
  terim,
  minUzunluk,
  durum,
  onGit,
  seciliIndeks,
  duzListe,
  turFiltresi,
  onFiltreTemizle,
}: SearchResultsProps) {
  /**
   * ⚠ HENÜZ YAZMAYA BAŞLAMADI ile SONUÇ YOK ayrı cümleler.
   *
   * İkisini tek boş duruma indirseydik, iki harf yazmış kullanıcı "sonuç bulunamadı"
   * okurdu ve aradığı şeyin olmadığına inanırdı — oysa arama daha çalışmamıştı.
   */
  if (terim.trim().length < minUzunluk) {
    return <p className="text-sm text-fg-soft">Aramak için en az {minUzunluk} karakter yazın.</p>;
  }

  if (durum.isPending) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-fg-soft">
        <Spinner size="sm" />
        Aranıyor…
      </div>
    );
  }

  /**
   * ⚠ HATA SESSİZCE BOŞ LİSTEYE DÖNÜŞMÜYOR. Boş liste "böyle bir kayıt yok" der; arama
   * düştüyse bu YANLIŞ bir cevaptır. Bu ürünün en pahalı kusur sınıfı (sessiz başarı)
   * tam olarak burada doğardı.
   */
  if (durum.isError) {
    return (
      <StateView
        state={resolveErrorState(durum.error, {
          idempotent: true,
          overrides: {
            error: {
              title: "Arama yapılamadı",
              description: "Bağlantı sorunu olabilir; yeniden deneyebilirsiniz.",
            },
          },
        })}
        onRetry={durum.refetch}
      />
    );
  }

  const hits = durum.data?.hits ?? [];

  if (hits.length === 0) {
    /**
     * ⚠ BOŞ SONUÇ AKTİF FİLTREYİ SÖYLÜYOR (CLOS-894 [S01]).
     *
     * Yalnız terimi yazsaydık, tür süzgeci açıkken hiçbir şey bulamayan kullanıcı "böyle
     * bir kayıt yok" sanırdı — oysa kayıt VAR olabilir ve yalnız süzgecin dışında
     * kalmıştır. İki cümle arasındaki fark, aramayı bırakmakla filtreyi kaldırmak
     * arasındaki fark.
     *
     * ⚠ Ve söylemek yetmiyor: kartın istediği AKSİYON da veriliyor. Kullanıcıya "filtren
     * yüzünden boş" deyip filtreyi kaldıracak yolu göstermemek, çıkmaz sokağı adıyla
     * anlatmaktan ibaret olurdu.
     */
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-fg-soft">
          <span className="font-medium text-fg-strong">“{terim.trim()}”</span> için
          {turFiltresi ? (
            <>
              {" "}
              <span className="font-medium text-fg-strong">
                {SEARCH_RECORD_TYPE_NAMES[turFiltresi]}
              </span>{" "}
              filtresiyle
            </>
          ) : null}{" "}
          sonuç bulunamadı.
        </p>
        {turFiltresi ? (
          <Button type="button" size="sm" variant="secondary" onClick={onFiltreTemizle}>
            Filtreleri temizle
          </Button>
        ) : null}
      </div>
    );
  }

  const gruplar = SIRA.map((tur) => ({
    tur,
    satirlar: hits.filter((h) => h.recordType === tur),
  })).filter((g) => g.satirlar.length > 0);

  return (
    <div className="flex flex-col gap-4">
      {gruplar.map((grup) => (
        <section key={grup.tur} className="flex flex-col gap-1">
          <h3 className="text-xs font-medium uppercase tracking-wide text-fg-faint">
            {SEARCH_RECORD_TYPE_NAMES[grup.tur]}
          </h3>
          <ul className="flex flex-col">
            {grup.satirlar.map((hit) => {
              const sira = duzListe.findIndex(
                (h) => h.recordType === hit.recordType && h.recordId === hit.recordId,
              );
              const secili = sira === seciliIndeks;
              return (
                <li key={`${hit.recordType}:${hit.recordId}`}>
                  <button
                    type="button"
                    /**
                     * ⚠ Kimlik ŞART: odak arama ALANINDA kaldığı için ekran okuyucu hangi
                     * seçeneğin etkin olduğunu ancak alanın `aria-activedescendant`'ından
                     * öğrenebiliyor ve o da bu kimliği gösteriyor. Kimliksiz bir seçenek
                     * listesinde görsel vurgu ilerlerken sesli takip yerinde sayardı.
                     */
                    id={secenekId(hit)}
                    /**
                     * ⚠ Klavye seçimi `aria-selected` ile de bildiriliyor: görsel vurgu tek
                     * başına ekran okuyucuya hiçbir şey söylemez ve kartın *"klavye ve mobil
                     * gezinim desteklenir"* kriteri yalnız görenler için karşılanmış olurdu.
                     */
                    aria-selected={secili}
                    role="option"
                    /**
                     * ⚠ Erişilebilir ad TÜRÜ de taşıyor (CLOS-894 [S01]). Tür yalnız grup
                     * başlığındaydı ve başlıkla satır arasında erişilebilir bir bağ yok:
                     * ekran okuyucu kullanıcısı başlığı duyup bunun sözleşme mi şablon mu
                     * olduğunu bilemezdi.
                     */
                    aria-label={`${SEARCH_RECORD_TYPE_NAMES[hit.recordType]}: ${hit.title}`}
                    onClick={() => onGit(hit)}
                    className={`flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${
                      secili ? "bg-muted" : ""
                    }`}
                  >
                    <span className="text-sm text-fg-strong">{hit.title}</span>
                    {hit.labels.length > 0 ? (
                      <span className="flex flex-wrap gap-1">
                        {hit.labels.map((etiket) => (
                          <Badge key={etiket} tone="neutral">
                            {etiket}
                          </Badge>
                        ))}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      {/**
       * ⚠ KESİLEN LİSTE SESSİZ KALMIYOR. Kullanıcı gördüğü listeyi "hepsi bu" sanarsa
       * aradığı kaydın olmadığına inanır. Sunucu `truncated` bayrağını bu yüzden dönüyor.
       */}
      {durum.data?.truncated ? (
        <p className="text-xs text-fg-faint">
          Sonuçların tamamı gösterilmiyor. Aramanızı daraltmayı deneyin.
        </p>
      ) : null}
    </div>
  );
}
